import {
  Component,
  ChangeDetectionStrategy,
  TemplateRef,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  EFLayoutDirection,
  EFLayoutMode,
  EFMarkerType,
  F_LAYOUT,
  FCanvasComponent,
  FFlowModule,
  FZoomDirective,
  IFLayoutWritebackPayload,
  provideFLayout
} from '@foblex/flow';
import { IPoint } from '@foblex/2d';
import { DagreLayoutEngine } from '@foblex/flow-dagre-layout';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import {
  AnchorTreeDecisionNode,
  AnchorTreeLeafNode,
  AnchorTreeNode
} from '../../types/anchor-tree';
import { CanvasNode, toCanvasGraph } from '../../utils/anchor-tree-canvas.utils';

/** Context passed to the projected `#leafContent` template (Templates page). */
export interface LeafContentContext {
  $implicit: AnchorTreeLeafNode;
}

/**
 * Reusable node-canvas rendering of the anchor tree (Foblex Flow): pan/zoom,
 * dagre auto-layout (layout owns positions — nodes are not user-draggable),
 * minimap, and branch-value edge chips. The Anchor Tree page uses it editable
 * (selection + add/delete actions); the Templates page uses it read-only with
 * template cards projected into leaves via `#leafContent`.
 */
@Component({
  selector: 'app-anchor-tree-canvas',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, MatIconModule, MatTooltipModule, FFlowModule],
  host: {
    // Trackpad pinch arrives as ctrl/cmd+wheel. Foblex only preventDefaults it
    // inside <f-flow>; over the toolbar/edges the BROWSER zooms the whole page
    // instead (and Chrome persists that zoom level across navigation). Contain
    // every zoom-intent wheel event to the canvas component.
    '(wheel)': 'onHostWheel($event)'
  },
  providers: [
    provideFLayout(DagreLayoutEngine, {
      mode: EFLayoutMode.AUTO,
      options: {
        direction: EFLayoutDirection.LEFT_RIGHT,
        nodeGap: 48,
        layerGap: 160
      }
    })
  ],
  styles: [`
    :host { display: block; height: 100%; position: relative; }
    f-flow { display: block; height: 100%; }
    /* Our Tailwind cards are the node chrome — neutralize the Foblex theme's
       node frame (fixed 120px width, padding, background, border, shadow),
       otherwise cards overflow their hosts and overlap each other. */
    :host ::ng-deep f-flow .f-node {
      width: max-content;
      min-height: 0;
      padding: 0;
      color: inherit;
      text-align: left;
      background: transparent;
      border: none;
      border-radius: 0;
      box-shadow: none;
    }
    /* Connectors are pure geometry anchors for the edges — invisible dots
       pinned to the vertical middle of the node's left/right sides so arrows
       land centered instead of at a corner. */
    :host ::ng-deep .anchor-canvas-socket {
      --ff-connector-size: 2px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      opacity: 0;
      pointer-events: none;
      border: none;
    }
    :host ::ng-deep .anchor-canvas-socket-in { left: 0; }
    :host ::ng-deep .anchor-canvas-socket-out { right: 0; }
    /* Edges are highlightable: hover recolors the path, click selects it
       (theme paints selected edges with the accent color). The label lives
       inside the f-connection element, so hovering it highlights the edge
       too — and both hover and selection recolor the label with it. */
    :host ::ng-deep f-flow .f-connection { cursor: pointer; }
    :host ::ng-deep f-flow:not(.f-dragging) .f-connection:not(.f-selected):hover .f-connection-path {
      stroke: var(--ff-color-accent);
    }
    :host ::ng-deep f-flow:not(.f-dragging) .f-connection:hover .f-connection-content span,
    :host ::ng-deep f-flow .f-connection.f-selected .f-connection-content span {
      color: var(--ff-color-accent);
    }
    :host ::ng-deep f-minimap {
      position: absolute;
      right: 12px;
      bottom: 12px;
      width: 140px;
      height: 96px;
    }
  `],
  template: `
    <f-flow
      fDraggable
      [fDraggableDisabled]="dragActive()"
      (fFullRendered)="onFullRendered()"
      (click)="onBackgroundClick($event)"
      data-testid="anchor-tree-canvas"
    >
      <f-background>
        <f-circle-pattern />
      </f-background>
      <f-canvas [fZoom]="!dragActive()" [fZoomMinimum]="0.2" [fZoomMaximum]="2">
        @for (edge of graph().edges; track edge.id) {
          <f-connection
            [fConnectionId]="edge.id"
            [fSourceId]="'out-' + edge.sourceId"
            [fTargetId]="'in-' + edge.targetId"
            fType="segment"
            fBehavior="fixed"
            [fReassignDisabled]="true"
          >
            <f-connection-marker-arrow [type]="markerEnd" />
            <div fConnectionContent [position]="0.5">
              @if (edge.label) {
                <span
                  class="px-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400 bg-white dark:bg-zinc-900 rounded"
                  [attr.data-testid]="'canvas-edge-label-' + edge.id"
                >
                  {{ edge.label }}
                </span>
              } @else {
                <span
                  class="px-1 text-[11px] font-medium italic text-amber-600 dark:text-amber-400 bg-white dark:bg-zinc-900 rounded"
                  [attr.data-testid]="'canvas-edge-label-' + edge.id"
                >
                  unset
                </span>
              }
            </div>
          </f-connection>
        }

        @for (cn of graph().nodes; track cn.id) {
          <div
            fNode
            [fNodeId]="cn.id"
            [fNodePosition]="positionFor(cn.id)"
            [fNodeDraggingDisabled]="true"
            [fNodeSelectionDisabled]="true"
            (click)="onNodeClick(cn.id, $event)"
          >
            <!-- fNodeInput/fNodeOutput (not the generic fConnector): the dagre
                 layout only sees connectors from the legacy input/output
                 registries, so fConnector edges would be invisible to it and
                 every node would land in a single overlapping layer. -->
            <div
              fNodeInput
              [fInputId]="'in-' + cn.id"
              fInputConnectableSide="left"
              class="anchor-canvas-socket anchor-canvas-socket-in"
            ></div>

            @if (cn.node.kind === 'decision') {
              <div
                class="w-56 cursor-pointer rounded-lg border bg-white dark:bg-zinc-800 p-3 shadow-sm transition-shadow"
                [class]="cn.id === selectedNodeId()
                  ? 'border-blue-500 ring-2 ring-blue-500/40'
                  : 'border-zinc-200 dark:border-zinc-700 hover:shadow-md'"
                [attr.data-testid]="'canvas-decision-' + cn.id"
              >
                <div class="flex items-center gap-1.5">
                  <mat-icon class="!text-base !w-4 !h-4 !leading-4 text-indigo-500 shrink-0">alt_route</mat-icon>
                  <span class="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {{ decisionFieldName(asDecision(cn.node)) }}
                  </span>
                  @if (decisionWarning(asDecision(cn.node)); as warning) {
                    <span
                      class="ml-auto h-2 w-2 shrink-0 rounded-full bg-amber-500"
                      [matTooltip]="warning"
                      [matTooltipDisabled]="dragActive()"
                      [attr.data-testid]="'canvas-decision-warning-' + cn.id"
                    ></span>
                  }
                </div>
                <div class="mt-1 flex items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
                  <span class="uppercase tracking-wide font-semibold">Decision</span>
                  @if (asDecision(cn.node).threshold !== undefined) {
                    <span matTooltip="Per-node routing threshold" [matTooltipDisabled]="dragActive()">≥ {{ asDecision(cn.node).threshold }}</span>
                  }
                  @if (cn.node.label) {
                    <span class="truncate">— {{ cn.node.label }}</span>
                  }
                </div>
                @if (editable()) {
                  <div class="mt-2 flex gap-1 border-t border-zinc-100 dark:border-zinc-700 pt-2">
                    <button
                      type="button"
                      matTooltip="Delete this node (and its subtree)"
                      (click)="onDelete(cn.id, $event)"
                      [attr.data-testid]="'canvas-delete-' + cn.id"
                      class="ml-auto flex h-5 w-5 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                    >
                      <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">delete</mat-icon>
                    </button>
                  </div>
                }
              </div>
              <div
                fNodeOutput
                [fOutputId]="'out-' + cn.id"
                [fOutputMultiple]="true"
                fOutputConnectableSide="right"
                class="anchor-canvas-socket anchor-canvas-socket-out"
              ></div>
            } @else {
              <div
                class="w-72 cursor-pointer rounded-lg border-2 border-dashed bg-indigo-50/40 dark:bg-indigo-900/10 p-3 transition-shadow"
                [style.width.rem]="leafWidthRem(asLeaf(cn.node))"
                [class]="cn.id === selectedNodeId()
                  ? 'border-blue-500 ring-2 ring-blue-500/40'
                  : 'border-indigo-200 dark:border-indigo-800 hover:shadow-md'"
                [attr.data-testid]="'canvas-leaf-' + cn.id"
              >
                <div class="flex items-center gap-1.5">
                  <mat-icon class="!text-base !w-4 !h-4 !leading-4 text-indigo-500 shrink-0">eco</mat-icon>
                  <span class="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {{ asLeaf(cn.node).label || 'Leaf' }}
                  </span>
                  @if (!editable()) {
                    <span class="ml-auto shrink-0 text-[11px] text-zinc-500 dark:text-zinc-400"
                          [attr.data-testid]="'canvas-leaf-template-count-' + cn.id">
                      {{ asLeaf(cn.node).templateIds.length }} template(s)
                    </span>
                  }
                  @if (editable()) {
                    <button
                      type="button"
                      matTooltip="Delete this leaf"
                      (click)="onDelete(cn.id, $event)"
                      [attr.data-testid]="'canvas-delete-' + cn.id"
                      class="ml-auto flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                    >
                      <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3">delete</mat-icon>
                    </button>
                  }
                </div>
                <div class="mt-1.5 flex flex-wrap gap-1"
                     matTooltip="Leaf anchor set — scored when matching documents at this leaf"
                     [matTooltipDisabled]="dragActive()">
                  @for (name of leafAnchorNames(asLeaf(cn.node)); track name) {
                    <span class="inline-flex items-center rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-100/60 dark:bg-indigo-900/30 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
                      ⚓ {{ name }}
                    </span>
                  } @empty {
                    <span class="text-[10px] text-zinc-400 dark:text-zinc-500">no leaf anchors</span>
                  }
                </div>
                @if (editable()) {
                  <div class="mt-1.5 text-[11px] text-zinc-500 dark:text-zinc-400"
                       [attr.data-testid]="'canvas-leaf-template-count-' + cn.id">
                    {{ asLeaf(cn.node).templateIds.length }} template(s) — assign on the Templates page
                  </div>
                }
                @if (leafContent(); as tpl) {
                  <div class="mt-2">
                    <ng-container *ngTemplateOutlet="tpl; context: { $implicit: asLeaf(cn.node) }" />
                  </div>
                }
              </div>
            }
          </div>
        }
      </f-canvas>
      <f-minimap [fMinSize]="1200" />
    </f-flow>

    <!-- Zoom / fit toolbar -->
    <div class="absolute left-3 top-3 z-10 flex flex-col gap-1 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 p-1 shadow-sm"
         data-testid="canvas-toolbar">
      <button type="button" matTooltip="Zoom in" (click)="zoom()?.zoomIn()"
              class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-zinc-500 dark:text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-700">
        <mat-icon class="!text-base !w-4 !h-4 !leading-4">add</mat-icon>
      </button>
      <button type="button" matTooltip="Zoom out" (click)="zoom()?.zoomOut()"
              class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-zinc-500 dark:text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-700">
        <mat-icon class="!text-base !w-4 !h-4 !leading-4">remove</mat-icon>
      </button>
      <button type="button" matTooltip="Fit tree to view" (click)="fit()" data-testid="canvas-fit-btn"
              class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-zinc-500 dark:text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-700">
        <mat-icon class="!text-base !w-4 !h-4 !leading-4">fit_screen</mat-icon>
      </button>
    </div>
  `
})
export class AnchorTreeCanvasComponent {
  readonly store = inject(OcrPrimeStore);
  private readonly layoutEngine = inject(F_LAYOUT);

  root = input.required<AnchorTreeNode | null>();
  selectedNodeId = input<string | null>(null);
  editable = input(false);
  /**
   * A card drag is in progress over the canvas: panning and zooming are locked
   * (CDK caches drop-list rects at drag start, so a moving canvas would make
   * the drop land on the wrong leaf) and node tooltips are suppressed so they
   * cannot pop up under the pointer and cover the drop target.
   */
  dragActive = input(false);

  nodeSelected = output<string | null>();
  deleteRequested = output<string>();

  /** Optional per-leaf projected content (template cards on the Templates page). */
  readonly leafContent = contentChild<TemplateRef<LeafContentContext>>('leafContent');

  protected readonly canvas = viewChild(FCanvasComponent);
  protected readonly zoom = viewChild(FZoomDirective);

  protected readonly markerEnd = EFMarkerType.END;

  protected readonly graph = computed(() => toCanvasGraph(this.root()));

  /** Dagre-computed positions, written back after every auto-layout run. */
  private readonly positions = signal<Record<string, IPoint>>({});

  private readonly anchorFieldIds = computed(
    () => new Set(this.store.fields().filter(f => f.category === 'anchor').map(f => f.id))
  );

  constructor() {
    // Layout owns positions: mirror every dagre run into the local map so the
    // [fNodePosition] bindings stay consistent, then re-fit — the layout only
    // re-runs on structural (node-id/edge-set) changes, so this is exactly the
    // "re-fit on structural change" hook.
    this.layoutEngine.setWriteback((payload: IFLayoutWritebackPayload) => {
      this.positions.update(current => {
        const next = { ...current };
        for (const { id, position } of payload.nodes) {
          next[id] = position;
        }
        return next;
      });
      // Skip fitting until the flow reported its first full render (FF1009).
      if (this.rendered) {
        requestAnimationFrame(() => this.fit(true));
      }
    });

    // The AUTO layout only re-runs on structural (node-id/edge-set) changes,
    // but node SIZES also change without structure changing — e.g. a template
    // card added to a leaf makes it taller and it overlaps its neighbors.
    // Force a relayout after such content changes so dagre re-measures.
    effect(() => {
      this.graph();
      this.store.templatesMap();
      if (!this.rendered) {
        return;
      }
      // Wait a frame so the DOM reflects the new content before measuring.
      requestAnimationFrame(() => this.layoutEngine.relayout());
    });
  }

  private rendered = false;

  protected positionFor(id: string): IPoint {
    return this.positions()[id] ?? { x: 0, y: 0 };
  }

  protected onFullRendered(): void {
    this.rendered = true;
    this.fit(false);
  }

  protected onHostWheel(event: WheelEvent): void {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
    }
  }

  protected fit(animated = true): void {
    this.canvas()?.fitToScreen({ x: 48, y: 48 }, animated, false);
  }

  protected onNodeClick(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.nodeSelected.emit(id);
  }

  protected onBackgroundClick(_event: MouseEvent): void {
    this.nodeSelected.emit(null);
  }

  protected onDelete(nodeId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.deleteRequested.emit(nodeId);
  }

  protected decisionFieldName(node: AnchorTreeDecisionNode): string {
    if (!node.decisiveAnchorFieldId) {
      return 'No decisive field';
    }
    return (
      this.store.fieldsByIdMap().get(node.decisiveAnchorFieldId)?.name ??
      `unknown field (${node.decisiveAnchorFieldId})`
    );
  }

  /** Warning-dot tooltip for a decision node, or null when it is healthy. */
  protected decisionWarning(node: AnchorTreeDecisionNode): string | null {
    if (!node.decisiveAnchorFieldId) {
      return 'No decisive anchor field selected';
    }
    const field = this.store.fieldsByIdMap().get(node.decisiveAnchorFieldId);
    if (!field) {
      return 'Decisive field no longer exists';
    }
    if (!this.anchorFieldIds().has(field.id)) {
      return 'Decisive field is not an anchor field';
    }
    if (field.type !== 'boolean' && !field.enumValues?.length) {
      return 'Decisive field is not enum/boolean';
    }
    if ((node.branches ?? []).some(b => !b.expectedValue)) {
      return 'Some branches have no expected value';
    }
    return null;
  }

  /**
   * Leaf width when template cards are projected in (Templates page): grows
   * with the card count so cards keep their fixed design width — one column
   * per template up to 3, then rows wrap. Null on the edit page (w-72 class).
   * Must stay in sync with the projected grid: 25rem columns + 0.75rem gap
   * (gap-3) + 0.75rem padding (p-3) each side + 2px borders.
   */
  protected leafWidthRem(leaf: AnchorTreeLeafNode): number | null {
    if (!this.leafContent()) {
      return null;
    }
    const cols = Math.min(Math.max(leaf.templateIds?.length ?? 0, 1), 3);
    return cols * 25 + (cols - 1) * 0.75 + 1.5 + 0.25;
  }

  protected leafAnchorNames(leaf: AnchorTreeLeafNode): string[] {
    const fields = this.store.fieldsByIdMap();
    return (leaf.leafAnchorFieldIds ?? []).map(id => fields.get(id)?.name ?? `unknown (${id})`);
  }

  protected asDecision(node: AnchorTreeNode): AnchorTreeDecisionNode {
    return node as AnchorTreeDecisionNode;
  }

  protected asLeaf(node: AnchorTreeNode): AnchorTreeLeafNode {
    return node as AnchorTreeLeafNode;
  }
}
